import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi, getToken, clearToken } from "@/lib/api";
import { toast } from "sonner";
import {
  Trash2,
  Eye,
  EyeOff,
  ArrowUp,
  ArrowDown,
  Plus,
  Save,
  Pencil,
  X,
  ExternalLink,
  FolderDown,
  LogOut,
  AlertTriangle,
} from "lucide-react";

export default function AdminPage() {
  const navigate = useNavigate();
  const [songs, setSongs] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  // Add song form
  const [newTitle, setNewTitle] = useState("");
  const [newLink, setNewLink] = useState("");
  const [newNote, setNewNote] = useState("");
  const [newType, setNewType] = useState("audio");
  const [adding, setAdding] = useState(false);

  // Settings form
  const [sHeading, setSHeading] = useState("");
  const [sIntro, setSIntro] = useState("");
  const [sClosed, setSClosed] = useState("");
  const [savingSettings, setSavingSettings] = useState(false);

  // Edit song modal
  const [editSong, setEditSong] = useState(null);

  // Folder import
  const [folderLink, setFolderLink] = useState(
    "https://drive.google.com/drive/folders/1bAUNBhLKkSxvTOlRCo7_olsWkW71caa6?usp=drive_link"
  );
  const [importing, setImporting] = useState(false);

  const load = async () => {
    try {
      const [songs, settings] = await Promise.all([
        adminApi.listSongs(),
        adminApi.getSettings(),
      ]);
      setSongs(songs);
      setSettings(settings);
      setSHeading(settings.heading || "");
      setSIntro(settings.intro || "");
      setSClosed(settings.closed_message || "");
    } catch (e) {
      if (e?.response?.status === 401) {
        navigate("/");
        return;
      }
      toast.error("Could not load admin data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!getToken()) {
      navigate("/");
      return;
    }
    load();
  }, []);

  const handleLogout = () => {
    clearToken();
    toast.success("Signed out");
    navigate("/");
  };

  const handleWipe = async () => {
    if (
      !window.confirm(
        "Wipe every song and video for next season? This cannot be undone."
      )
    ) {
      return;
    }
    const pwd = window.prompt("Enter the wipe password to confirm:");
    if (pwd === null) return; // user cancelled
    if (!pwd) {
      toast.error("Wipe password is required");
      return;
    }
    try {
      const res = await adminApi.wipeSongs(pwd);
      toast.success(`Deleted ${res.deleted} items`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not wipe items");
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newTitle.trim() || !newLink.trim()) {
      toast.error("Title and Google Drive link are required");
      return;
    }
    setAdding(true);
    try {
      await adminApi.createSong({
        title: newTitle,
        drive_link: newLink,
        note: newNote,
        media_type: newType,
      });
      toast.success(newType === "video" ? "Video added" : "Song added");
      setNewTitle("");
      setNewLink("");
      setNewNote("");
      setNewType("audio");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add song");
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this song?")) return;
    try {
      await adminApi.deleteSong(id);
      toast.success("Song deleted");
      await load();
    } catch {
      toast.error("Could not delete song");
    }
  };

  const handleToggleVisible = async (song) => {
    try {
      await adminApi.updateSong(song.id, { visible: !song.visible });
      await load();
    } catch {
      toast.error("Could not update song");
    }
  };

  const handleMove = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= songs.length) return;
    const ids = songs.map((s) => s.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    try {
      const updated = await adminApi.reorderSongs(ids);
      setSongs(updated);
    } catch {
      toast.error("Could not reorder");
    }
  };

  const handleTogglePage = async () => {
    if (!settings) return;
    try {
      const next = await adminApi.updateSettings({ is_open: !settings.is_open });
      setSettings(next);
      toast.success(next.is_open ? "Page opened" : "Page closed");
    } catch {
      toast.error("Could not update page status");
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const next = await adminApi.updateSettings({
        heading: sHeading,
        intro: sIntro,
        closed_message: sClosed,
      });
      setSettings(next);
      toast.success("Page wording saved");
    } catch {
      toast.error("Could not save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editSong) return;
    try {
      await adminApi.updateSong(editSong.id, {
        title: editSong.title,
        drive_link: editSong.drive_link,
        note: editSong.note,
        media_type: editSong.media_type,
      });
      toast.success("Item updated");
      setEditSong(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update item");
    }
  };

  const handleImportFolder = async () => {
    if (!folderLink.trim()) {
      toast.error("Paste a Google Drive folder link first");
      return;
    }
    setImporting(true);
    try {
      const res = await adminApi.importFolder(folderLink.trim());
      const parts = [
        `Imported ${res.imported}`,
        res.skipped_duplicate ? `skipped ${res.skipped_duplicate} duplicate` : null,
        res.skipped_other_type ? `ignored ${res.skipped_other_type} non-media` : null,
      ].filter(Boolean);
      toast.success(parts.join(", "));
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-[color:var(--rr-blue)] font-heading">Loading admin...</div>
      </div>
    );
  }

  return (
    <div data-testid="admin-page" className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-[color:var(--rr-blue)] text-white">
        <div className="max-w-4xl mx-auto px-5 py-6 flex items-center gap-4">
          <img src="/assets/logo.png" alt="Rolling River" className="h-12 w-12 rounded-full bg-white p-1" />
          <div className="flex-1">
            <h1 className="font-heading text-2xl sm:text-3xl font-bold">Song Admin</h1>
            <p className="text-sm text-white/80">Rolling River Day Camp</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm hidden sm:inline">
              Page is {settings?.is_open ? "open" : "closed"}
            </span>
            <button
              data-testid="toggle-page-btn"
              onClick={handleTogglePage}
              className={`px-3 py-2 rounded-full text-sm font-semibold transition ${
                settings?.is_open
                  ? "bg-white text-[color:var(--rr-blue)] hover:bg-slate-100"
                  : "bg-[color:var(--rr-gold)] text-[color:var(--rr-blue-dark)] hover:brightness-95"
              }`}
            >
              {settings?.is_open ? "Close page" : "Open page"}
            </button>
            <button
              data-testid="logout-btn"
              onClick={handleLogout}
              className="p-2 rounded-full text-white hover:bg-white/10 transition"
              aria-label="Sign out"
              title="Sign out"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-5 py-6 sm:py-8 space-y-8">
        {/* Bulk import from Drive folder */}
        <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-6">
          <h2 className="font-heading text-xl font-bold text-[color:var(--rr-blue)] mb-3 flex items-center gap-2">
            <FolderDown className="w-5 h-5" /> Import from a Drive folder
          </h2>
          <p className="text-sm text-slate-600 mb-3">
            Paste a Google Drive folder link. Every audio and video file inside will be added.
            Non media files are ignored, and files already added are skipped.
          </p>
          <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-start">
            <input
              data-testid="import-folder-link"
              value={folderLink}
              onChange={(e) => setFolderLink(e.target.value)}
              placeholder="https://drive.google.com/drive/folders/..."
              className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-[color:var(--rr-blue)] outline-none font-mono text-sm"
            />
            <button
              data-testid="import-folder-btn"
              onClick={handleImportFolder}
              disabled={importing}
              className="px-5 py-3 rounded-full bg-[color:var(--rr-gold)] text-[color:var(--rr-blue-dark)] font-semibold hover:brightness-95 transition disabled:opacity-60 inline-flex items-center gap-2 justify-center"
            >
              <FolderDown className="w-4 h-4" />
              {importing ? "Importing..." : "Import folder"}
            </button>
          </div>
        </section>
        {/* Add song */}
        <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-6">
          <h2 className="font-heading text-xl font-bold text-[color:var(--rr-blue)] mb-4 flex items-center gap-2">
            <Plus className="w-5 h-5" /> Add a song or video
          </h2>
          <form onSubmit={handleAdd} className="grid gap-3">
            <input
              data-testid="new-song-title"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="Title"
              className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-[color:var(--rr-blue)] outline-none"
            />
            <input
              data-testid="new-song-link"
              value={newLink}
              onChange={(e) => setNewLink(e.target.value)}
              placeholder="Paste Google Drive share link"
              className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-[color:var(--rr-blue)] outline-none font-mono text-sm"
            />
            <input
              data-testid="new-song-note"
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Optional note or description"
              className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-[color:var(--rr-blue)] outline-none"
            />
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-slate-700 mr-1">Type:</span>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="new-media-type"
                  data-testid="new-type-audio"
                  value="audio"
                  checked={newType === "audio"}
                  onChange={() => setNewType("audio")}
                />
                <span className="text-sm">Song (audio)</span>
              </label>
              <label className="inline-flex items-center gap-2 cursor-pointer ml-3">
                <input
                  type="radio"
                  name="new-media-type"
                  data-testid="new-type-video"
                  value="video"
                  checked={newType === "video"}
                  onChange={() => setNewType("video")}
                />
                <span className="text-sm">Video</span>
              </label>
            </div>
            <button
              data-testid="add-song-btn"
              type="submit"
              disabled={adding}
              className="justify-self-start px-5 py-2.5 rounded-full bg-[color:var(--rr-blue)] text-white font-semibold hover:bg-[color:var(--rr-blue-dark)] transition disabled:opacity-60"
            >
              {adding ? "Adding..." : "Add item"}
            </button>
          </form>
        </section>

        {/* Songs list */}
        <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-6">
          <h2 className="font-heading text-xl font-bold text-[color:var(--rr-blue)] mb-4">
            Songs ({songs.length})
          </h2>
          {songs.length === 0 ? (
            <p className="text-slate-500 text-sm">No songs yet. Add one above.</p>
          ) : (
            <ul data-testid="admin-song-list" className="divide-y divide-slate-100">
              {songs.map((s, i) => (
                <li
                  key={s.id}
                  data-testid={`admin-song-${s.id}`}
                  className="py-3 flex items-center gap-3"
                >
                  <div className="flex flex-col gap-1">
                    <button
                      data-testid={`move-up-${s.id}`}
                      onClick={() => handleMove(i, -1)}
                      disabled={i === 0}
                      aria-label="Move up"
                      className="p-1 rounded hover:bg-slate-100 text-slate-600 disabled:opacity-30"
                    >
                      <ArrowUp className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`move-down-${s.id}`}
                      onClick={() => handleMove(i, 1)}
                      disabled={i === songs.length - 1}
                      aria-label="Move down"
                      className="p-1 rounded hover:bg-slate-100 text-slate-600 disabled:opacity-30"
                    >
                      <ArrowDown className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-[color:var(--rr-blue)] truncate">{s.title}</p>
                      <span
                        className={`flex-none inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${
                          s.media_type === "video"
                            ? "bg-[color:var(--rr-gold)] text-[color:var(--rr-blue-dark)]"
                            : "bg-[color:var(--rr-blue-light)] text-[color:var(--rr-blue)]"
                        }`}
                      >
                        {s.media_type === "video" ? "Video" : "Song"}
                      </span>
                    </div>
                    {s.note && <p className="text-sm text-slate-600 truncate">{s.note}</p>}
                    <a
                      href={s.drive_link}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-slate-500 hover:text-[color:var(--rr-blue)] inline-flex items-center gap-1 mt-0.5"
                    >
                      Drive link <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      data-testid={`toggle-visible-${s.id}`}
                      onClick={() => handleToggleVisible(s)}
                      aria-label={s.visible ? "Hide" : "Show"}
                      title={s.visible ? "Hide from public page" : "Show on public page"}
                      className={`p-2 rounded-full transition ${
                        s.visible
                          ? "bg-[color:var(--rr-blue-light)] text-[color:var(--rr-blue)]"
                          : "bg-slate-100 text-slate-400"
                      }`}
                    >
                      {s.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </button>
                    <button
                      data-testid={`edit-song-${s.id}`}
                      onClick={() => setEditSong({ ...s })}
                      aria-label="Edit"
                      className="p-2 rounded-full text-slate-600 hover:bg-slate-100"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`delete-song-${s.id}`}
                      onClick={() => handleDelete(s.id)}
                      aria-label="Delete"
                      className="p-2 rounded-full text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Settings */}
        <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-6">
          <h2 className="font-heading text-xl font-bold text-[color:var(--rr-blue)] mb-4">
            Page wording
          </h2>
          <div className="grid gap-3">
            <label className="block">
              <span className="text-sm font-medium text-slate-700">Heading</span>
              <input
                data-testid="settings-heading"
                value={sHeading}
                onChange={(e) => setSHeading(e.target.value)}
                className="mt-1 w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-[color:var(--rr-blue)] outline-none"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-slate-700">Intro message</span>
              <textarea
                data-testid="settings-intro"
                value={sIntro}
                onChange={(e) => setSIntro(e.target.value)}
                rows={3}
                className="mt-1 w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-[color:var(--rr-blue)] outline-none"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-slate-700">Closed page message</span>
              <textarea
                data-testid="settings-closed"
                value={sClosed}
                onChange={(e) => setSClosed(e.target.value)}
                rows={2}
                className="mt-1 w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-[color:var(--rr-blue)] outline-none"
              />
            </label>
            <button
              data-testid="save-settings-btn"
              onClick={handleSaveSettings}
              disabled={savingSettings}
              className="justify-self-start px-5 py-2.5 rounded-full bg-[color:var(--rr-blue)] text-white font-semibold hover:bg-[color:var(--rr-blue-dark)] transition disabled:opacity-60 inline-flex items-center gap-2"
            >
              <Save className="w-4 h-4" /> {savingSettings ? "Saving..." : "Save wording"}
            </button>
          </div>
        </section>

        {/* Danger zone: wipe for next season */}
        <section className="bg-white rounded-2xl border-2 border-red-200 shadow-sm p-5 sm:p-6">
          <h2 className="font-heading text-xl font-bold text-red-700 mb-2 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" /> End of season
          </h2>
          <p className="text-sm text-slate-600 mb-4">
            Wipe every song and video to start fresh for the next season. Page wording and open
            or closed status are preserved. This cannot be undone and requires the wipe password.
          </p>
          <button
            data-testid="wipe-btn"
            onClick={handleWipe}
            className="px-5 py-2.5 rounded-full bg-red-600 text-white font-semibold hover:bg-red-700 transition inline-flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" /> Wipe all songs and videos
          </button>
        </section>

        <p className="text-center text-xs text-slate-400">
          Public page:{" "}
          <a href="/" className="underline hover:text-[color:var(--rr-blue)]">
            open public view
          </a>
        </p>
      </main>

      {editSong && (
        <EditSongModal
          song={editSong}
          onChange={setEditSong}
          onCancel={() => setEditSong(null)}
          onSave={handleSaveEdit}
        />
      )}
    </div>
  );
}

function EditSongModal({ song, onChange, onCancel, onSave }) {
  return (
    <div
      data-testid="edit-modal"
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-xl font-bold text-[color:var(--rr-blue)]">Edit song</h3>
          <button
            data-testid="close-edit-btn"
            onClick={onCancel}
            className="p-2 rounded-full hover:bg-slate-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="grid gap-3">
          <input
            data-testid="edit-title"
            value={song.title}
            onChange={(e) => onChange({ ...song, title: e.target.value })}
            placeholder="Title"
            className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-[color:var(--rr-blue)] outline-none"
          />
          <input
            data-testid="edit-link"
            value={song.drive_link}
            onChange={(e) => onChange({ ...song, drive_link: e.target.value })}
            placeholder="Google Drive share link"
            className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-[color:var(--rr-blue)] outline-none font-mono text-sm"
          />
          <input
            data-testid="edit-note"
            value={song.note || ""}
            onChange={(e) => onChange({ ...song, note: e.target.value })}
            placeholder="Optional note"
            className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-[color:var(--rr-blue)] outline-none"
          />
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-700 mr-1">Type:</span>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="edit-media-type"
                data-testid="edit-type-audio"
                value="audio"
                checked={(song.media_type || "audio") === "audio"}
                onChange={() => onChange({ ...song, media_type: "audio" })}
              />
              <span className="text-sm">Song (audio)</span>
            </label>
            <label className="inline-flex items-center gap-2 cursor-pointer ml-3">
              <input
                type="radio"
                name="edit-media-type"
                data-testid="edit-type-video"
                value="video"
                checked={song.media_type === "video"}
                onChange={() => onChange({ ...song, media_type: "video" })}
              />
              <span className="text-sm">Video</span>
            </label>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button
            data-testid="cancel-edit-btn"
            onClick={onCancel}
            className="px-4 py-2 rounded-full border border-slate-300 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            data-testid="save-edit-btn"
            onClick={onSave}
            className="px-5 py-2 rounded-full bg-[color:var(--rr-blue)] text-white font-semibold hover:bg-[color:var(--rr-blue-dark)]"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
